import { z } from "zod";
import { format } from "date-fns";

const optional = () => z.string().optional().or(z.literal(""));
const optionalNum = () =>
  z.preprocess((v) => {
    if (v === "" || v == null) return null;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }, z.number().nonnegative().nullable().optional());

export const poLineSchema = z.object({
  id: z.string().optional(),
  material_id: z.string().min(1, "Material required"),
  material_grade_id: optional(),
  shape_size_id: optional(),
  description: optional(),
  qty: z.preprocess((v) => {
    if (v === "" || v == null) return 0;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.round(n));
  }, z.number().int().min(0)),
  weight: z.coerce.number().min(0),
  uom_id: optional(),
  unit_rate: z.coerce.number().min(0),
  gst_slab: z.coerce.number().min(0).max(100),
  thickness: optionalNum(),
  width: optionalNum(),
  length: optionalNum(),
  od: optionalNum(),
  inbound_process_type_id: optional(),
});

export const purchaseOrderFormSchema = z
  .object({
    vendor_id: z.string().min(1, "Vendor is required"),
    issue_date: z.string().min(1),
    due_date: optional(),
    payment_terms: optional(),
    bill_to_text: optional(),
    ship_to_text: optional(),
    discount: z.coerce.number().min(0),
    adjustment: z.coerce.number(),
    notes: optional(),
    terms: optional(),
    lines: z.array(poLineSchema).min(1, "Add at least one line"),
  })
  .superRefine((values, ctx) => {
    values.lines.forEach((line, i) => {
      if (line.qty <= 0 && line.weight <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lines", i, "weight"],
          message: "Enter qty or weight",
        });
      }
    });
  });

export type PoLineFormValues = z.infer<typeof poLineSchema>;
export type PurchaseOrderFormValues = z.infer<typeof purchaseOrderFormSchema>;

export function emptyPoLine(): PoLineFormValues {
  return {
    material_id: "",
    material_grade_id: "",
    shape_size_id: "",
    description: "",
    qty: 0,
    weight: 0,
    uom_id: "",
    unit_rate: 0,
    gst_slab: 18,
    thickness: null,
    width: null,
    length: null,
    od: null,
    inbound_process_type_id: "",
  };
}

export const emptyPurchaseOrderForm: PurchaseOrderFormValues = {
  vendor_id: "",
  issue_date: format(new Date(), "yyyy-MM-dd"),
  due_date: "",
  payment_terms: "",
  bill_to_text: "",
  ship_to_text: "",
  discount: 0,
  adjustment: 0,
  notes: "",
  terms: "",
  lines: [emptyPoLine()],
};
